import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Slide,
  Stack,
  TextField,
  Typography,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Add,
  Close as CloseIcon,
  AccountBalanceWallet,
} from '@mui/icons-material';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="left" in={props.open} ref={ref} {...props} />;
});

export default function AddWallet({ open, onClose }) {
  const [openDialog, setOpenDialog] = useState(false);

  // Form state
  const [walletData, setWalletData] = useState({
    userId: '',
    name: '',
    walletType: '',
    initialBalance: '',
    currency: 'USD ($)',
    description: '',
    isActive: true,
  });

  const handleInputChange = (field, value) => {
    setWalletData({
      ...walletData,
      [field]: value
    });
  };

  const handleClickOpen = () => {
    setOpenDialog(true);
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      setOpenDialog(false);
    }
  };

  // Define reusable input styling - copied from RecJobs for consistency
  const inputProps = {
    sx: {
      bgcolor: "#f9f9f9",
      "& .MuiOutlinedInput-root": {
        "& fieldset": {
          borderColor: "#e0e0e0",
        },
        "&:hover fieldset": {
          borderColor: "#bdbdbd",
        },
      },
      "& .MuiSelect-select": {
        color: "#424242",
      },
      "& .MuiInputBase-input": {
        color: "#424242",
      },
    },
  };

  const checkboxStyle = {
    color: "#9e9e9e",
    "&.Mui-checked": {
      color: "#4285F4",
    },
  };

  // Use either provided open state or internal state
  const isOpen = open !== undefined ? open : openDialog;

  const walletTypes = [
    { value: "personal", label: "Personal Wallet" },
    { value: "business", label: "Business Wallet" },
    { value: "savings", label: "Savings Wallet" },
    { value: "investment", label: "Investment Wallet" },
  ];

  const currencies = [
    { value: "USD ($)", label: "USD ($)" },
    { value: "EUR (€)", label: "EUR (€)" },
    { value: "INR (₹)", label: "INR (₹)" },
  ];

  return (
    <div>
      {/* Only render the button if no open prop is provided (standalone mode) */}
      {open === undefined && (
        <Stack direction="row" spacing={2}>
          <Button 
            variant="contained" 
            startIcon={<Add />}
            onClick={handleClickOpen}
            sx={{
              bgcolor: "#4285F4",
              color: "white",
              "&:hover": {
                bgcolor: "#3367D6",
              },
              textTransform: "none",
              fontWeight: 500,
            }}
          >
            Add Wallet
          </Button>
        </Stack>
      )}

      <Dialog
        open={isOpen}
        onClose={handleClose}
        TransitionComponent={Transition}
        maxWidth="lg"
        PaperProps={{
          style: {
            position: "absolute",
            top: 0,
            right: 0,
            width: "85%",
            height: "100%",
            margin: 0,
            borderRadius: 0,
          }
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            right: 0,
            width: "100%",
            height: "100%",
            bgcolor: "#f5f5f5",
            boxShadow: 24,
            p: 4,
            overflow: "auto",
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={3}
          >
            <Typography variant="h3" component="h2" sx={{ color: "#212121", display: "flex", alignItems: "center" }}>
              <AccountBalanceWallet sx={{ mr: 2, fontSize: 36 }} />
              Wallet Details
            </Typography>
            <Button
              onClick={handleClose}
              variant="outlined"
              sx={{
                borderColor: "#bdbdbd",
                color: "#424242",
                "&:hover": {
                  borderColor: "#9e9e9e",
                  bgcolor: "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              Close
            </Button>
          </Box>

          <Card
            sx={{
              p: 2,
              bgcolor: "#ffffff",
              boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.08)",
            }}
          >
            <CardContent>
              <Box sx={{ mb: 4 }}>
                <Grid container spacing={3}>
                  {/* User ID */}
                  <Grid item xs={12} md={4}>
                    <Typography variant="body1" mb={1} sx={{ color: "#212121" }}>
                      User ID <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="Enter User ID"
                      size="small"
                      value={walletData.userId}
                      onChange={(e) => handleInputChange('userId', e.target.value)}
                      InputProps={inputProps}
                    />
                  </Grid>
                  
                  {/* Name */}
                  <Grid item xs={12} md={4}>
                    <Typography variant="body1" mb={1} sx={{ color: "#212121" }}>
                      Wallet Name <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="Enter wallet name"
                      size="small"
                      value={walletData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      InputProps={inputProps}
                    />
                  </Grid>
                  
                  {/* Wallet Type */}
                  <Grid item xs={12} md={4}>
                    <Typography variant="body1" mb={1} sx={{ color: "#212121" }}>
                      Wallet Type <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <TextField
                      select
                      fullWidth
                      value={walletData.walletType}
                      onChange={(e) => handleInputChange('walletType', e.target.value)}
                      size="small"
                      placeholder="Select wallet type"
                      InputProps={inputProps}
                    >
                      <MenuItem value="">--</MenuItem>
                      {walletTypes.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  {/* Initial Balance */}
                  <Grid item xs={12} md={4}>
                    <Typography variant="body1" mb={1} sx={{ color: "#212121" }}>
                      Initial Balance <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <TextField
                      fullWidth
                      type="number"
                      placeholder="0.00"
                      size="small"
                      value={walletData.initialBalance}
                      onChange={(e) => handleInputChange('initialBalance', e.target.value)}
                      InputProps={{
                        ...inputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            $
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  
                  {/* Currency */}
                  <Grid item xs={12} md={4}>
                    <Typography variant="body1" mb={1} sx={{ color: "#212121" }}>
                      Currency
                    </Typography>
                    <TextField
                      select
                      fullWidth
                      value={walletData.currency}
                      onChange={(e) => handleInputChange('currency', e.target.value)}
                      size="small"
                      InputProps={inputProps}
                    >
                      {currencies.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  
                  {/* Status */}
                  <Grid item xs={12} md={4}>
                    <Box sx={{ mt: 4 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            sx={checkboxStyle}
                            checked={walletData.isActive}
                            onChange={(e) => handleInputChange('isActive', e.target.checked)}
                          />
                        }
                        label="Active wallet"
                        sx={{ color: "#757575" }}
                      />
                    </Box>
                  </Grid>

                  {/* Description */}
                  <Grid item xs={12}>
                    <Typography variant="body1" mb={1} sx={{ color: "#212121" }}>
                      Description
                    </Typography>
                    <TextField
                      multiline
                      rows={4}
                      fullWidth
                      placeholder="Enter wallet details here..."
                      value={walletData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      variant="outlined"
                      sx={{
                        "& fieldset": {
                          border: "1px solid #e0e0e0",
                          borderRadius: "4px",
                        },
                        "& .MuiInputBase-root": {
                          color: "#212121",
                        },
                        bgcolor: "#f9f9f9",
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Form buttons */}
              <Box display="flex" gap={2} mt={3}>
                <Button
                  variant="contained"
                  sx={{
                    bgcolor: "#4285F4",
                    color: "white",
                    "&:hover": { bgcolor: "#3367D6" },
                    fontWeight: 500,
                    textTransform: "none",
                    padding: "8px 16px",
                  }}
                >
                  Save
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleClose}
                  sx={{
                    borderColor: "#bdbdbd",
                    color: "#424242",
                    "&:hover": {
                      borderColor: "#9e9e9e",
                      bgcolor: "rgba(0, 0, 0, 0.04)",
                    },
                    textTransform: "none",
                    padding: "8px 16px",
                  }}
                >
                  Cancel
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Dialog>
    </div>
  );
}

